import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { CalendarCheckIcon, FileTextIcon, HandshakeIcon, PhoneCallIcon, SparklesIcon, TrendingUpIcon, UserPlusIcon, UsersIcon, XCircleIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/crm/PageHeader';
import { StatCard } from '../../components/crm/StatCard';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
const CHART_COLORS = ['#3D8BFF', '#6EA8F7', '#0B1F4D', '#F59E0B', '#16A34A', '#DC2626'];
function ChartCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-border bg-background p-6 shadow-soft" data-id="element-662" data-mp="pm47fm9mj9bx">
      <h2 className="mb-6 text-sm font-semibold text-foreground" data-id="element-663" data-mp="zeyxl4tw543z">{title}</h2>
      <div className="h-64 w-full" data-id="element-664" data-mp="13fixuh9rgnuc">{children}</div>
    </section>;
}
export function Dashboard() {
  const {
    leads,
    stages,
    landings,
    forms,
    isDemo
  } = useWorkspace();

  /**
   * Las métricas se calculan a partir de los estados configurados por la
   * empresa: si se renombran o se crean nuevos, el dashboard se adapta solo.
   */
  const stats = useMemo(() => {
    const initial = stages.find(s => s.isInitial) ?? stages[0];
    const advanced = leads.filter(l => l.stageId !== initial?.id).length;
    return {
      total: leads.length,
      nuevos: leads.filter(l => l.stageId === initial?.id).length,
      initialName: initial?.name ?? 'Nuevo',
      byStage: stages.filter(s => s.active && s.id !== initial?.id).map(stage => ({
        stage,
        count: leads.filter(l => l.stageId === stage.id).length
      })),
      conversion: leads.length ? `${Math.round(advanced / leads.length * 100)}%` : '0%'
    };
  }, [leads, stages]);
  const byDay = useMemo(() => {
    const days: {
      fecha: string;
      leads: number;
    }[] = [];
    for (let i = 13; i >= 0; i -= 1) {
      const date = new Date(Date.now() - i * 86400000);
      const key = date.toISOString().slice(0, 10);
      days.push({
        fecha: date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short'
        }),
        leads: leads.filter(l => l.createdAt.slice(0, 10) === key).length
      });
    }
    return days;
  }, [leads]);
  const byWeek = useMemo(() => {
    const weeks: {
      semana: string;
      leads: number;
    }[] = [];
    for (let i = 7; i >= 0; i -= 1) {
      const start = Date.now() - (i + 1) * 7 * 86400000;
      const end = Date.now() - i * 7 * 86400000;
      weeks.push({
        semana: `S-${i}`,
        leads: leads.filter(l => {
          const time = new Date(l.createdAt).getTime();
          return time > start && time <= end;
        }).length
      });
    }
    return weeks;
  }, [leads]);
  const bySource = useMemo(() => {
    const map = new Map<string, number>();
    leads.forEach(l => map.set(l.origin.source || 'directo', (map.get(l.origin.source || 'directo') ?? 0) + 1));
    return [...map.entries()].map(([name, value]) => ({
      name,
      value
    }));
  }, [leads]);
  const byLanding = useMemo(() => landings.map(landing => ({
    name: landing.name.length > 18 ? `${landing.name.slice(0, 18)}…` : landing.name,
    leads: leads.filter(l => l.origin.landingId === landing.id).length
  })), [landings, leads]);
  const byForm = useMemo(() => forms.map(form => ({
    name: form.name.length > 18 ? `${form.name.slice(0, 18)}…` : form.name,
    leads: leads.filter(l => l.origin.formId === form.id).length
  })), [forms, leads]);
  const byStage = useMemo(() => stages.map(stage => ({
    name: stage.name,
    leads: leads.filter(l => l.stageId === stage.id).length,
    color: stage.color
  })), [stages, leads]);
  if (leads.length === 0) {
    return <>
        <PageHeader title="Dashboard" description="Resumen de la captación de tu empresa." data-id="element-665" data-mp="16vkg5d3a191j" />
        <EmptyState icon={<SparklesIcon className="h-5 w-5" data-id="element-667" data-mp="11vpp99ji4jgh" />} title="Todavía no hay datos que mostrar" description="Publica una landing o comparte un formulario para empezar a recibir leads y ver aquí tus métricas." action={<Link to="/app/landings" data-id="element-668" data-mp="sy5mtx9wkh4a">
              <Button data-id="element-669" data-mp="nvwr4t5nb0yh">Crear una landing</Button>
            </Link>} data-id="element-666" data-mp="11x0rv617nl60" />
      </>;
  }
  return <>
      <PageHeader title="Dashboard" description="Resumen de la captación de tu empresa." actions={isDemo ? <DemoBadge data-id="element-671" data-mp="2i8mwc71grwl6" /> : undefined} data-id="element-670" data-mp="iywtn6mv36iz" />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-672" data-mp="2db4mmefmhdc4">
        <StatCard label="Total de leads" value={stats.total} icon={<UsersIcon className="h-4 w-4" data-id="element-674" data-mp="2lrvgrlp8w1ox" />} data-id="element-673" data-mp="2d9thaa6j3xiw" />
        <StatCard label={stats.initialName} value={stats.nuevos} icon={<UserPlusIcon className="h-4 w-4" data-id="element-676" data-mp="2ps5ob2sor05w" />} data-id="element-675" data-mp="2lqu2l8cxmz2z" />
        <StatCard label="Tasa de conversión" value={stats.conversion} icon={<TrendingUpIcon className="h-4 w-4" data-id="element-678" data-mp="2zj0a6620rxqt" />} hint="Leads que han avanzado del primer estado" data-id="element-677" data-mp="2um02xrtfooy4" />
        {stats.byStage.map(({
        stage,
        count
      }) => <StatCard key={stage.id} label={stage.name} value={count} icon={<PhoneCallIcon className="h-4 w-4" data-id="element-680" data-mp="2btxswzbas6wu" />} accent={stage.color} data-id="element-679" data-mp="26v4d308jg3ti" />)}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2" data-id="element-681" data-mp="2gulhlec20bnw">
        <ChartCard title="Leads por día (últimas 2 semanas)" data-id="element-682" data-mp="3n9mh1u0afbz8">
          <ResponsiveContainer width="100%" height="100%" data-id="element-683" data-mp="3n94aa3g01fd2">
            <LineChart data={byDay} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-684" data-mp="3s87ghr7ptrmm">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-685" data-mp="32kl7le9s1d7k" />
              <XAxis dataKey="fecha" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-686" data-mp="32mefeww4m3h5" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-687" data-mp="2xnusat5sqln7" />
              <ChartTooltip data-id="element-688" data-mp="3cgesbkxngd0f" />
              <Line type="monotone" dataKey="leads" stroke="#3D8BFF" strokeWidth={2} dot={false} data-id="element-689" data-mp="37jeolgo9vmch" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por semana" data-id="element-690" data-mp="2ev0qz213is1g">
          <ResponsiveContainer width="100%" height="100%" data-id="element-691" data-mp="2ewbsev72jnpe">
            <BarChart data={byWeek} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-692" data-mp="2tjn3yx3937pi">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-693" data-mp="2tlq449y4ah7y" />
              <XAxis dataKey="semana" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-694" data-mp="2or3qs18h6el8" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-695" data-mp="211hqfagd9tbn" />
              <ChartTooltip data-id="element-696" data-mp="1w2exgodnvdk1" />
              <Bar dataKey="leads" fill="#3D8BFF" radius={[4, 4, 0, 0]} data-id="element-697" data-mp="bmqj4nno9owv" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por fuente" data-id="element-698" data-mp="gertg41a55k9">
          <ResponsiveContainer width="100%" height="100%" data-id="element-699" data-mp="1okkcex5wyw8">
            <PieChart data-id="element-700" data-mp="1q4106mqdbyj">
              <Pie data={bySource} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} data-id="element-701" data-mp="1mga7f8o73ni">
                {bySource.map((entry, index) => <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} data-id="element-702" data-mp="udrihcspfmgy" data-mpi={index} />)}
              </Pie>
              <ChartTooltip data-id="element-703" data-mp="lca6a5if44xj" />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversión por estado" data-id="element-704" data-mp="pdvc56wjbiy8">
          <ResponsiveContainer width="100%" height="100%" data-id="element-705" data-mp="pck8r8704d7n">
            <BarChart data={byStage} layout="vertical" margin={{
            left: 40,
            right: 16
          }} data-id="element-706" data-mp="362r6z1mx2a3b">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} data-id="element-707" data-mp="3a447cw78pl2u" />
              <XAxis type="number" allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-708" data-mp="5m9tdkepdhca" />
              <YAxis type="category" dataKey="name" width={110} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-709" data-mp="motn7rpl4yi" />
              <ChartTooltip data-id="element-710" data-mp="3ss0phqswea6x" />
              <Bar dataKey="leads" radius={[0, 4, 4, 0]} data-id="element-711" data-mp="3sr95ld8ny0wh">
                {byStage.map(entry => <Cell key={entry.name} fill={entry.color} data-id="element-712" data-mp="3ntz8dbf18ky3" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por landing" data-id="element-713" data-mp="kcgzd0nix7w3">
          <ResponsiveContainer width="100%" height="100%" data-id="element-714" data-mp="fkh7lvhlxlal">
            <BarChart data={byLanding} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-715" data-mp="fiwapz7tlq9k">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-716" data-mp="akbcu6g1jioa" />
              <XAxis dataKey="name" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-717" data-mp="181tjuwoax2dp" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-718" data-mp="139rx2gtibt9f" />
              <ChartTooltip data-id="element-719" data-mp="1alzjis5vu6og" />
              <Bar dataKey="leads" fill="#0B1F4D" radius={[4, 4, 0, 0]} data-id="element-720" data-mp="1an0yv8txlfbz" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por formulario" data-id="element-721" data-mp="1kjm7s592nal3">
          <ResponsiveContainer width="100%" height="100%" data-id="element-722" data-mp="1khszklit8iaz">
            <BarChart data={byForm} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-723" data-mp="1ol7pc3wjwd1m">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-724" data-mp="1tkbxcr7qo4qd" />
              <XAxis dataKey="name" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-725" data-mp="1yj5aujn0zvqj" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-726" data-mp="239vgzz3a02ma" />
              <ChartTooltip data-id="element-727" data-mp="288ymfkn64aus" />
              <Bar dataKey="leads" fill="#6EA8F7" radius={[4, 4, 0, 0]} data-id="element-728" data-mp="289848ky27bpe" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>;
}